import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/query-keys';
import { apiFetch } from '@/lib/api/client';

export class DeleteCityError extends Error {
  constructor(
    message: string,
    public status?: number
  ) {
    super(message);
    this.name = 'DeleteCityError';
  }
}

interface DeleteCityResult {
  success: boolean;
}

function parseErrorMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object' && 'error' in (payload as Record<string, unknown>)) {
    const error = (payload as Record<string, { message?: string }>).error;
    if (error && typeof error.message === 'string' && error.message.trim().length > 0) {
      return error.message;
    }
  }
  return fallback;
}

async function deleteCity(tripId: string, cityId: number): Promise<DeleteCityResult> {
  const response = await apiFetch(`/api/v1/business-trip-requests/${tripId}/cities/${cityId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const message = parseErrorMessage(payload, 'Failed to delete city');
    throw new DeleteCityError(message, response.status);
  }

  return { success: true };
}

export function useDeleteCity(tripId: string) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (cityId: number) => deleteCity(tripId, cityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tripCities(tripId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.trip(tripId) });
    },
  });

  return mutation;
}
